import { SocketClient } from "cbcore-ts"
import * as pako from "pako"
import { IF, NO, UIBaseButton, UIColor, UIRectangle, UITextView, UIView, YES } from "uicore-ts"
import { RHFileMetadata } from "../../SocketClientFunctions"
import { RHButton } from "./RHButton"
import { RHColor } from "./RHColor"
import { RHFileItemView } from "./RHFileItemView"
import { RHFileListView } from "./RHFileListView"
import { RHForms } from "./Utils_slop/RHDesignTokens"
import { messageOfError } from "../../Core/RHErrorMessage"

export interface UploadedFile {
    id: string
    name: string
    type: string
    size: number
    compressedSize?: number
}

export interface RHAllowedFileType {
    displayName: string
    extensions: string[]
    mimeTypes: string[]
}

interface PresignedUploadData {
    url: string
    fields: Record<string, string>
    fileId: string
}


// ─── Drop Zone ──────────────────────────────────────────────────────────────

class RHDropZone extends UIBaseButton {
    
    readonly label = new UITextView()
        .configuredWithObject({
            text: "📎 Lisa failid  ·  kuni 10 MB  ·  PDF, DOC, DOCX, XLS, XLSX, PNG, JPG",
            backgroundColor: UIColor.transparentColor,
            isSingleLine: NO,
            textAlignment: UITextView.textAlignment.center,
            // fontSize: 13,
            fontSize: RHForms.fileUploadDropZoneLabelFontSize,
            userInteractionEnabled: NO,
            style: {
                // fontWeight: "500",
                fontWeight: RHForms.fileUploadDropZoneLabelFontWeight
            }
        })
        .addedAsSubviewToView(this)
    
    constructor(elementID?: string) {
        super(elementID)
        ;(this as RHDropZone).configureWithObject({
            style: {
                // borderRadius: "8px",
                borderRadius: RHForms.fileUploadDropZoneBorderRadius,
                // border: `2px dashed ${RHColor.borderLight.stringValue}`,
                border: `${RHForms.fileUploadDropZoneBorderWidth} ${RHForms.fileUploadDropZoneBorderStyle} ${RHForms.fileUploadDropZoneBorderColor.stringValue}`,
                outline: "none",
                boxShadow: "none",
                cursor: "pointer",
                // transition: "all 0.2s ease"
                transition: RHForms.fileUploadDropZoneTransition
            }
        })
    }
    
    override updateContentForFocusedState() {
        super.updateContentForFocusedState()
        if (!this.hovered) {
            this.updateContentForNormalState()
        }
    }
    
    override updateContentForNormalState() {
        super.updateContentForNormalState()
        ;(this as RHDropZone).configureWithObject({
            // backgroundColor: RHColor.surface,
            backgroundColor: RHForms.fileUploadDropZoneBackgroundColor,
            style: {
                // @ts-ignore
                // borderColor: RHColor.borderLight.stringValue
                borderColor: RHForms.fileUploadDropZoneBorderColor.stringValue
            }
        })
        // this.label.textColor = RHColor.textSecondary
        this.label.textColor = RHForms.fileUploadDropZoneLabelTextColor
    }
    
    override updateContentForHoveredState() {
        super.updateContentForHoveredState()
        ;(this as RHDropZone).configureWithObject({
            // backgroundColor: RHColor.primary.colorWithAlpha(0.03),
            backgroundColor: new UIColor(RHForms.fileUploadDropZoneHoverBackgroundColor),
            style: {
                // @ts-ignore
                borderColor: RHColor.primary.stringValue
            }
        })
        // this.label.textColor = RHColor.primary
        this.label.textColor = RHForms.fileUploadDropZoneHoverLabelTextColor
    }
    
    override updateContentForHighlightedState() {
        super.updateContentForHighlightedState()
        ;(this as RHDropZone).configureWithObject({
            // backgroundColor: RHColor.primary.colorWithAlpha(0.05),
            backgroundColor: new UIColor(RHForms.fileUploadDropZoneActiveBackgroundColor),
            style: {
                // @ts-ignore
                borderColor: RHColor.primary.stringValue
            }
        })
        // this.label.textColor = RHColor.primary
        this.label.textColor = RHForms.fileUploadDropZoneHoverLabelTextColor
    }
    
    override updateContentForCurrentEnabledState() {
        super.updateContentForCurrentEnabledState()
        if (!this.enabled) {
            ;(this as RHDropZone).configureWithObject({
                backgroundColor: RHColor.background,
                style: {
                    // @ts-ignore
                    borderColor: RHColor.borderLight.stringValue,
                    // opacity: "0.6",
                    opacity: RHForms.fileUploadDropZoneDisabledOpacity,
                    cursor: "not-allowed"
                }
            })
            this.label.textColor = RHColor.textMuted
        }
        else {
            ;(this as RHDropZone).configureWithObject({
                style: {
                    opacity: "1",
                    cursor: "pointer"
                }
            })
            this.updateContentForNormalState()
        }
    }
    
    override layoutSubviews() {
        super.layoutSubviews()
        this.label.weakFrame = this.bounds
    }
    
}


// ─── File Upload View ────────────────────────────────────────────────────────

export class RHFileUploadView extends UIView {

    static readonly defaultAllowedFileTypes: RHAllowedFileType[] = [
        {
            displayName: "PDF",
            extensions: [".pdf"],
            mimeTypes: ["application/pdf"]
        },
        {
            displayName: "DOC",
            extensions: [".doc"],
            mimeTypes: ["application/msword"]
        },
        {
            displayName: "DOCX",
            extensions: [".docx"],
            mimeTypes: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"]
        },
        {
            displayName: "XLS",
            extensions: [".xls"],
            mimeTypes: ["application/vnd.ms-excel"]
        },
        {
            displayName: "XLSX",
            extensions: [".xlsx"],
            mimeTypes: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"]
        },
        {
            displayName: "PNG",
            extensions: [".png"],
            mimeTypes: ["image/png"]
        },
        {
            displayName: "JPG",
            extensions: [".jpg", ".jpeg"],
            mimeTypes: ["image/jpeg"]
        }
    ]

    static readonly allowedFileTypesForProcurementDocuments: RHAllowedFileType[] = [
        ...RHFileUploadView.defaultAllowedFileTypes,
        {
            displayName: "ASICE",
            extensions: [".asice"],
            mimeTypes: ["application/vnd.etsi.asic-e+zip"]
        },
        {
            displayName: "BDOC",
            extensions: [".bdoc"],
            mimeTypes: ["application/vnd.etsi.asic-e+zip", "application/x-bdoc"]
        },
        {
            displayName: "DDOC",
            extensions: [".ddoc"],
            mimeTypes: ["application/x-ddoc"]
        },
        {
            displayName: "CSV",
            extensions: [".csv"],
            mimeTypes: ["text/csv", "application/vnd.ms-excel"]
        }
    ]

    static readonly procurementOfferAllowedFileTypes: RHAllowedFileType[] = [
        ...RHFileUploadView.allowedFileTypesForProcurementDocuments,
        {
            displayName: "ODT",
            extensions: [".odt"],
            mimeTypes: ["application/vnd.oasis.opendocument.text"]
        },
        {
            displayName: "TXT",
            extensions: [".txt"],
            mimeTypes: ["text/plain"]
        },
        {
            displayName: "ZIP",
            extensions: [".zip"],
            mimeTypes: ["application/zip", "application/x-zip-compressed"]
        }
    ]
    
    static zipFolderIcon(): string {
        const iconSize = RHForms.fileUploadBulkDownloadButtonIconSize
        // Old folder-with-zipper icon — read ambiguously (looked like a padlock at small size):
        // return `<svg xmlns="http://www.w3.org/2000/svg" width="${iconSize}" height="${iconSize}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0" aria-hidden="true">
        //   <path d="M3.2 8.4A2.4 2.4 0 0 1 5.6 6h4.3l2 2.1h6.5A2.4 2.4 0 0 1 20.8 10.5v6A2.4 2.4 0 0 1 18.4 19H5.6A2.4 2.4 0 0 1 3.2 16.6V8.4Z"/>
        //   <path d="M3.4 10.4h17.2"/>
        //   <rect x="8.2" y="12.6" width="7.6" height="5" rx="1.1"/>
        //   <path d="M10 12.6v-1.3a2 2 0 0 1 4 0v1.3"/>
        //   <path d="M12 14.8v1"/>
        // </svg>`
        // New archive-box icon (Feather-style): lid + box + slot = a packaged ZIP bundle.
        return `<svg xmlns="http://www.w3.org/2000/svg" width="${iconSize}" height="${iconSize}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0" aria-hidden="true">
  <rect x="2.5" y="3.5" width="19" height="4.5" rx="1"/>
  <path d="M4.5 8v11.5a1 1 0 0 0 1 1h13a1 1 0 0 0 1-1V8"/>
  <path d="M9.5 12h5"/>
</svg>`
    }

    static bulkDownloadTitle(label = "Laadi alla kõik failid (ZIP)"): string {
        return `<span style="display:inline-flex;align-items:center;justify-content:flex-start;gap:${RHForms.fileUploadBulkDownloadButtonTitleGap}px;white-space:nowrap">
  ${RHFileUploadView.zipFolderIcon()}
  <span>${label}</span>
</span>`
    }

    static variant = {
        full: "full" as const,
        compact: "compact" as const
    }
    
    _variant: "full" | "compact" = "full"
    
    get variant() { return this._variant }
    set variant(value: "full" | "compact") {
        this._variant = value
        this.setNeedsLayout()
    }
    
    readonly fileInput: HTMLInputElement
    
    readonly dropZone = new RHDropZone()
        .addedAsSubviewToView(this)
    
    readonly statusLabel = new UITextView()
        .configuredWithObject({
            text: "Maksimaalne failisuurus: 10 MB · PDF, DOC, DOCX, XLS, XLSX, PNG, JPG",
            backgroundColor: UIColor.transparentColor,
            isSingleLine: NO,
            textAlignment: UITextView.textAlignment.center,
            // fontSize: 12,
            fontSize: RHForms.fileUploadStatusFontSize,
            // textColor: RHColor.textMuted
            textColor: RHForms.fileUploadStatusTextColor
        })
        .addedAsSubviewToView(this)
    
    fileItemViews: RHFileItemView[] = []
    
    readonly bulkDownloadButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "" },
            enabled: YES,
            backgroundColor: RHColor.primary,
            style: {
                color: RHColor.textWhite.stringValue,
                textAlign: "left"
            }
        })

    readonly bulkDownloadContentLabel = new UIView()
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            userInteractionEnabled: NO,
            style: {
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                color: RHColor.textWhite.stringValue,
                lineHeight: "1",
                pointerEvents: "none",
                whiteSpace: "nowrap",
                overflow: "hidden"
            }
        })
        .addedAsSubviewToView(this.bulkDownloadButton)
    
    _uploadedFiles: UploadedFile[] = []
    _maxFileSize = 10 * 1024 * 1024 // 10MB
    _allowedFileTypes = RHFileUploadView.defaultAllowedFileTypes
    _isShowingError = NO
    
    _compressionEnabled = true
    _onFilesChanged?: (files: UploadedFile[]) => void
    // Tracks the in-flight handleFileSelection() call (if any) so a caller like
    // the offer save flow can await waitForPendingUploads() and find out
    // whether it's safe to proceed, rather than reading uploadedFiles before
    // an upload that hasn't settled yet has had a chance to land in it.
    _activeFileSelectionPromise: Promise<void> | undefined
    _lastFileSelectionHadFailure = false
    // Bulk "download all as ZIP" suits procurement documents but not every
    // consumer (e.g. feedback attachments) — opt out per instance.
    bulkDownloadEnabled = YES
    shouldDeleteFilesWhenRemovedFromList = YES
    
    // True while a file is being dragged over the drop zone itself
    _isDropZoneDragging = false
    // True while any file is being dragged anywhere on the document
    _isDocumentDragging = false
    _dragCounter = 0
    
    // Tracks whether the file-picker dialog is open
    _isFileDialogOpen = false
    
    
    constructor(elementID?: string) {
        
        super(elementID);
        
        (this as RHFileUploadView).configureWithObject({
            // backgroundColor: RHColor.surface,
            backgroundColor: RHForms.fileUploadBackgroundColor,
            style: {
                // borderRadius: "8px",
                borderRadius: RHForms.fileUploadBorderRadius,
                // border: `1px solid ${RHColor.border.stringValue}`,
                border: `1px solid ${RHForms.fileUploadBorderColor.stringValue}`,
                // boxShadow: `0 1px 3px ${RHColor.shadow.stringValue}`
                boxShadow: RHForms.fileUploadBoxShadow
            }
        })
        
        // Create hidden file input
        this.fileInput = document.createElement("input")
        this.fileInput.type = "file"
        this.fileInput.multiple = true
        this.fileInput.style.display = "none"
        this.viewHTMLElement.appendChild(this.fileInput)
        this._applyAllowedFileTypes()
        
        this._configureBulkDownloadButtonContent()
        this.setupEvents()
    }
    
    
    _stopFileDialogLoadingIndicator() {
        if (!this._isFileDialogOpen) { return }
        this._isFileDialogOpen = false
        this.loading = NO
        this.dropZone.focus()
    }
    
    
    setupEvents() {
        // File input change — fires when the user picks files (dialog closed with a selection)
        this.fileInput.addEventListener("change", async () => {
            this._stopFileDialogLoadingIndicator()
            await this.handleFileSelection(Array.from(this.fileInput.files || []))
        })
        
        // Click to upload
        // noinspection JSConstantReassignment
        this.dropZone.controlEventTargetAccumulator.PointerUpInside = () => {
            if (!this.enabled) { return }
            
            this._isFileDialogOpen = true
            this.loading = YES
            this.fileInput.click()
            
            // The window regains focus when the OS file dialog is dismissed (with or
            // without a selection). One-time listener: whichever fires first wins.
            const onWindowFocus = () => {
                // Give the browser one tick so the "change" event (if any) fires first.
                setTimeout(() => { this._stopFileDialogLoadingIndicator() }, 300)
                window.removeEventListener("focus", onWindowFocus)
            }
            window.addEventListener("focus", onWindowFocus)
        }
        
        // Enter key on the focused drop zone opens the file picker
        this.dropZone.addTargetForControlEvent(UIView.controlEvent.EnterDown, () => {
            if (!this.enabled) { return }
            this._isFileDialogOpen = true
            this.loading = YES
            this.fileInput.click()
            const onWindowFocus = () => {
                setTimeout(() => { this._stopFileDialogLoadingIndicator() }, 300)
                window.removeEventListener("focus", onWindowFocus)
            }
            window.addEventListener("focus", onWindowFocus)
        })
        
        // Bulk download
        // noinspection JSConstantReassignment
        this.bulkDownloadButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            await this.downloadAllAsZip()
        }
        
        this._setupDocumentDragDetection()
        this._setupDropZoneDragHandling()
    }
    
    
    // ─── Document-level drag ────────────────────────────────────────────────────
    
    _setupDocumentDragDetection() {
        
        let dragOverTimer: ReturnType<typeof setTimeout> | undefined
        
        const endDocumentDrag = () => {
            clearTimeout(dragOverTimer)
            this._dragCounter = 0
            if (this._isDocumentDragging) {
                this._isDocumentDragging = false
                this.setNeedsLayout()
            }
        }
        
        document.addEventListener("dragenter", () => {
            this._dragCounter++
            if (this._dragCounter === 1) {
                this._isDocumentDragging = true
                this.setNeedsLayout()
            }
        })
        
        document.addEventListener("dragleave", () => {
            this._dragCounter = Math.max(0, this._dragCounter - 1)
            if (this._dragCounter === 0) {
                endDocumentDrag()
            }
        })
        
        document.addEventListener("dragover", (e) => {
            e.preventDefault()
            clearTimeout(dragOverTimer)
            dragOverTimer = setTimeout(endDocumentDrag, 200)
        })
        
        document.addEventListener("dragend", endDocumentDrag)
        document.addEventListener("drop", (e) => {
            e.preventDefault()
            endDocumentDrag()
        })
    }
    
    
    // ─── Drop-zone drag ─────────────────────────────────────────────────────────
    
    _setupDropZoneDragHandling() {
        const el = this.dropZone.viewHTMLElement
        
        el.addEventListener("dragover", (e) => { e.preventDefault(); e.stopPropagation() })
        
        el.addEventListener("dragenter", (e) => {
            e.preventDefault()
            e.stopPropagation()
            if (!this._isDropZoneDragging && this.enabled) {
                this._isDropZoneDragging = true
                this._updateDropZoneDragVisuals()
            }
        })
        
        el.addEventListener("dragleave", (e) => {
            e.stopPropagation()
            this._isDropZoneDragging = false
            this._updateDropZoneDragVisuals()
        })
        
        el.addEventListener("drop", async (e: DragEvent) => {
            e.preventDefault()
            e.stopPropagation()
            this._dragCounter = 0
            this._isDocumentDragging = false
            this._isDropZoneDragging = false
            this._updateDropZoneDragVisuals()
            this.setNeedsLayout()
            if (this.enabled) {
                const files = Array.from(e.dataTransfer?.files || [])
                await this.handleFileSelection(files)
            }
        })
    }
    
    
    _updateDropZoneDragVisuals() {
        this.dropZone.highlighted = this._isDropZoneDragging
    }
    
    
    setOnFilesChanged(callback: (files: UploadedFile[]) => void) {
        this._onFilesChanged = callback
    }
    
    
    get uploadedFiles(): UploadedFile[] {
        return this._uploadedFiles
    }


    get allowedFileTypes(): RHAllowedFileType[] {
        return this._allowedFileTypes
    }

    set allowedFileTypes(allowedFileTypes: RHAllowedFileType[]) {
        this._allowedFileTypes = allowedFileTypes
        this._applyAllowedFileTypes()
    }
    
    
    override get enabled(): boolean {
        return this._enabled
    }
    
    override set enabled(enabled: boolean) {
        this._enabled = enabled
        this.dropZone.enabled = enabled
        this.fileInput.disabled = !enabled
        this.fileItemViews.forEach(fileItemView => {
            fileItemView.deleteButton.enabled = enabled
        })
    }
    
    
    initWithExistingFiles(files: (RHFileMetadata | UploadedFile)[]) {
        this.fileItemViews.forEach(view => view.removeFromSuperview())
        this.fileItemViews = []
        
        this._uploadedFiles = files.map(file => ({
            id: file.id,
            name: file.name,
            type: file.type,
            size: file.size
        }))
        
        files.forEach(file => {
            const fileItemView = new RHFileItemView(
                undefined,
                (fileItemView: RHFileItemView) => this.fileItemDidDeleteFile(fileItemView)
            ).configuredWithObject({
                shouldDeleteFileWhenRemovedFromList: this.shouldDeleteFilesWhenRemovedFromList,
                deleteButton: { enabled: this.enabled }
            })
            fileItemView.configureWithFile(file)
            this.fileItemViews.push(fileItemView)
            fileItemView.addedAsSubviewToView(this)
        })
        
        if (files.length > 1 && this.bulkDownloadEnabled) {
            this.bulkDownloadButton.addedAsSubviewToView(this)
        }

        this.setNeedsLayoutUpToRootView()
    }
    
    
    clearFiles() {
        this._uploadedFiles = []
        this.fileItemViews.forEach(view => view.removeFromSuperview())
        this.fileItemViews = []
        this.bulkDownloadButton.removeFromSuperview()
        this.setNeedsLayoutUpToRootView()
    }
    
    
    async fileItemDidDeleteFile(fileItemView: RHFileItemView) {
        
        try {
            
            this._uploadedFiles = this._uploadedFiles.filter(f => f.id !== fileItemView._fileId)
            
            if (fileItemView) {
                fileItemView.removeFromSuperview()
                this.fileItemViews = this.fileItemViews.filter(v => v !== fileItemView)
                this.setNeedsLayoutUpToRootView()
            }
            
            if (this._uploadedFiles.length > 1 && this.bulkDownloadEnabled) {
                this.bulkDownloadButton.addedAsSubviewToView(this)
            }
            else {
                this.bulkDownloadButton.removeFromSuperview()
            }
            
            if (this._onFilesChanged) {
                this._onFilesChanged(this._uploadedFiles)
            }
            
        }
        catch (error) {
            console.error("Delete error:", error)
            this.showError("Viga faili kustutamisel")
        }
        
    }
    
    
    async handleFileSelection(files: File[]) {
        const selectionPromise = this._performFileSelection(files)
        this._activeFileSelectionPromise = selectionPromise
        await selectionPromise
    }


    async _performFileSelection(files: File[]) {
        if (files.length === 0 || !this.enabled) {
            return
        }

        this._lastFileSelectionHadFailure = false

        for (const file of files) {
            const validationError = this.validateFile(file)
            if (validationError) {
                this.showError(validationError)
                this._lastFileSelectionHadFailure = true
                continue
            }

            const uploadSucceeded = await this.uploadFile(file)
            if (!uploadSucceeded) {
                this._lastFileSelectionHadFailure = true
            }
        }

        this.fileInput.value = ""
        if (this._uploadedFiles.length > 1 && this.bulkDownloadEnabled) {
            this.bulkDownloadButton.addedAsSubviewToView(this)
        }
        else {
            this.bulkDownloadButton.removeFromSuperview()
        }

        if (this._onFilesChanged) {
            this._onFilesChanged(this._uploadedFiles)
        }
    }


    /**
     * Waits for whatever file selection is currently in flight (if any), then
     * reports whether every file in that batch uploaded successfully. Callers
     * that need to save/submit the surrounding form should await this first -
     * otherwise a file that's still mid-upload silently won't be in
     * uploadedFiles yet, and a failed upload won't be reflected at all since
     * its item view is removed on failure.
     */
    async waitForPendingUploads(): Promise<boolean> {
        if (this._activeFileSelectionPromise) {
            await this._activeFileSelectionPromise
        }
        return !this._lastFileSelectionHadFailure
    }
    
    
    validateFile(file: File): string | null {
        if (file.size > this._maxFileSize) {
            return `Fail „${file.name}“ on liiga suur. Maksimaalne failisuurus on 10 MB.`
        }
        
        if (!this._acceptedMimeTypeForFileNameAndReportedMimeType(file.name, file.type)) {
            return `Fail "${file.name}" tüüp ei ole lubatud. ` +
                `Lubatud failitüübid: ${this._allowedFileTypesText()}.`
        }
        
        return null
    }
    
    
    async uploadFile(file: File): Promise<boolean> {
        const fileType = this._acceptedMimeTypeForFileNameAndReportedMimeType(file.name, file.type)
        if (!fileType) {
            this.showError(
                `Fail "${file.name}" tüüp ei ole lubatud. ` +
                `Lubatud failitüübid: ${this._allowedFileTypesText()}.`
            )
            return false
        }
        const fileItemView = new RHFileItemView(
            undefined,
            (fileItemView: RHFileItemView) => this.fileItemDidDeleteFile(fileItemView))
            .configuredWithObject({
                shouldDeleteFileWhenRemovedFromList: this.shouldDeleteFilesWhenRemovedFromList,
                deleteButton: { enabled: this.enabled }
            })
        fileItemView.configureAsUploading(file.name)
        this.fileItemViews.push(fileItemView)
        fileItemView.addedAsSubviewToView(this)
        this.setNeedsLayoutUpToRootView()
        
        try {
            const { processedFile, wasCompressed } =
                await this.maybeCompressFile(file, fileType)
            
            const presignedData = (await SocketClient.GetPresignedUploadUrl({
                fileName: file.name,
                fileType,
                fileSize: processedFile.size,
                wasCompressed
            })).result
            
            if (!presignedData) {
                throw new Error("Failed to get upload URL")
            }
            
            await this.uploadToStorage(
                processedFile,
                presignedData,
                (progress) => { fileItemView.updateProgress(progress) }
            )
            
            const confirmResult = (await SocketClient.ConfirmFileUpload({
                fileId: presignedData.fileId
            })).result
            
            if (!confirmResult?.success) {
                throw new Error("Upload confirmation failed")
            }
            
            this._uploadedFiles.push({
                id: presignedData.fileId,
                name: file.name,
                type: fileType,
                size: file.size,
                compressedSize: wasCompressed ? processedFile.size : undefined
            })
            
            fileItemView.configureWithFileIdAndName(presignedData.fileId, file.name)
            this.setNeedsLayoutUpToRootView()
            return true

        }
        catch (error) {
            console.error("Upload error:", error)
            fileItemView.removeFromSuperview()
            this.fileItemViews = this.fileItemViews.filter(v => v !== fileItemView)
            this.showError(`Viga faili üleslaadimisel: ${messageOfError(error) ?? ""}`)
            this.setNeedsLayoutUpToRootView()
            return false
        }
    }
    
    
    async maybeCompressFile(file: File, fileType = file.type): Promise<{ processedFile: File | Blob; wasCompressed: boolean; compressionRatio: number }> {
        if (!this._compressionEnabled || !fileType.startsWith("image/")) {
            return { processedFile: file, wasCompressed: false, compressionRatio: 1 }
        }
        
        try {
            const arrayBuffer = await file.arrayBuffer()
            const uint8Array = new Uint8Array(arrayBuffer)
            const compressed = pako.deflate(uint8Array)
            const compressionRatio = compressed.length / uint8Array.length
            
            if (compressionRatio >= 0.95) {
                return { processedFile: file, wasCompressed: false, compressionRatio: 1 }
            }
            
            const compressedBlob = new Blob([compressed], { type: "application/octet-stream" })
            return { processedFile: compressedBlob, wasCompressed: true, compressionRatio }
        }
        catch {
            return { processedFile: file, wasCompressed: false, compressionRatio: 1 }
        }
    }
    
    
    async uploadToStorage(
        file: File | Blob,
        presignedData: PresignedUploadData,
        onProgress: (progress: number) => void
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            const formData = new FormData()
            Object.entries(presignedData.fields).forEach(([key, value]) => {
                formData.append(key, value)
            })
            formData.append("file", file)
            
            const xhr = new XMLHttpRequest()
            
            xhr.upload.addEventListener("progress", (e) => {
                if (e.lengthComputable) {
                    onProgress(Math.round((e.loaded / e.total) * 100))
                }
            })
            
            xhr.addEventListener("load", () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve()
                }
                else {
                    reject(new Error(`Upload failed with status ${xhr.status}`))
                }
            })
            
            xhr.addEventListener("error", () => { reject(new Error("Upload network error")) })
            xhr.addEventListener("abort", () => { reject(new Error("Upload aborted")) })
            
            xhr.open("POST", presignedData.url)
            xhr.send(formData)
        })
    }
    
    
    async downloadAllAsZip() {
        if (this._uploadedFiles.length === 0) { return }
        
        try {
            this.bulkDownloadButton.enabled = NO
            this.bulkDownloadContentLabel.viewHTMLElement.innerHTML = RHFileUploadView.bulkDownloadTitle("Laadin alla…")
            
            await RHFileListView.downloadFilesAsZip(this._uploadedFiles.map(f => f.id))
        }
        catch (error) {
            console.error("Bulk download error:", error)
            this.showError(`Viga failide allalaadimisel: ${messageOfError(error) ?? ""}`)
        }
        finally {
            this.bulkDownloadButton.enabled = YES
            this.bulkDownloadContentLabel.viewHTMLElement.innerHTML = RHFileUploadView.bulkDownloadTitle()
        }
    }


    _configureBulkDownloadButtonContent() {
        this.bulkDownloadButton.hoverText = "Laadi alla kõik failid (ZIP)"
        this.bulkDownloadButton.contentPadding = RHForms.fileUploadBulkDownloadButtonContentPadding
        this.bulkDownloadButton.style.textAlign = "left"
        this.bulkDownloadContentLabel.viewHTMLElement.innerHTML = RHFileUploadView.bulkDownloadTitle()
        this.bulkDownloadButton.titleLabel.hidden = YES
        this.bulkDownloadButton.titleLabel.textAlignment = UITextView.textAlignment.left
        this.bulkDownloadButton.titleLabel.configureWithObject({
            style: {
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                textAlign: "left"
            }
        })
        this._layoutBulkDownloadButtonContent()
    }


    _layoutBulkDownloadButtonContent() {
        const padding = RHForms.fileUploadBulkDownloadButtonContentPadding
        const buttonWidth = this.bulkDownloadButton.frame.width || this.bulkDownloadButton.bounds.width
        const buttonHeight = this.bulkDownloadButton.frame.height || this.bulkDownloadButton.bounds.height
        const contentWidth = Math.max(0, buttonWidth - padding * 2)

        this.bulkDownloadContentLabel.configureWithObject({
            style: {
                left: `${padding}px`,
                top: "0px",
                width: `${contentWidth}px`,
                height: `${buttonHeight}px`,
                transform: "none"
            }
        })
    }
    
    
    showError(message: string) {
        this._isShowingError = YES
        this.statusLabel.text = "⚠ " + message
        this.statusLabel.configureWithObject({
            style: { color: RHForms.fileUploadErrorTextColor.stringValue }
        })
        this.setNeedsLayoutUpToRootView()
        
        setTimeout(() => {
            this._isShowingError = NO
            this.statusLabel.text = this._statusText()
            this.statusLabel.configureWithObject({
                style: { color: RHForms.fileUploadStatusTextColor.stringValue }
            })
            this.setNeedsLayoutUpToRootView()
        }, 5000)
    }


    _applyAllowedFileTypes() {
        this._isShowingError = NO
        const allowedFileTypesText = this._allowedFileTypesText()
        this.fileInput.accept = this._fileInputAcceptValue()
        this.dropZone.label.text =
            `📎 Lisa failid  ·  kuni 10 MB  ·  ${allowedFileTypesText}`
        this.statusLabel.text = this._statusText()
        this.setNeedsLayout()
    }


    _allowedFileTypesText(): string {
        return this.allowedFileTypes.map(fileType => fileType.displayName).join(", ")
    }


    _statusText(): string {
        return `Maksimaalne failisuurus: 10 MB. Lubatud failitüübid: ${this._allowedFileTypesText()}.`
    }


    _fileInputAcceptValue(): string {
        const acceptedValues = this.allowedFileTypes
            .flatMap(fileType => [...fileType.extensions, ...fileType.mimeTypes])
        return [...new Set(acceptedValues)].join(",")
    }


    _acceptedMimeTypeForFileNameAndReportedMimeType(fileName: string, reportedMimeType: string): string | undefined {
        const normalizedFileName = fileName.trim().toLowerCase()
        const fileType = this.allowedFileTypes.find(candidate =>
            candidate.extensions.some(extension => normalizedFileName.endsWith(extension))
        )
        if (!fileType) {
            return undefined
        }

        const normalizedMimeType = reportedMimeType.split(";", 1)[0].trim().toLowerCase()
        if (!normalizedMimeType || normalizedMimeType === "application/octet-stream") {
            return fileType.mimeTypes[0]
        }
        if (fileType.mimeTypes.includes(normalizedMimeType)) {
            return normalizedMimeType
        }
        return undefined
    }
    
    
    // ─── Layout ─────────────────────────────────────────────────────────────────
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(
            IF(this._variant === "compact")(() => 0).ELSE(() => padding)
        )
        const hasFiles = this.fileItemViews.length > 0
        
        if (this._variant === "compact") {
            
            if (this._isDocumentDragging || !hasFiles) {
                
                this.fileItemViews.everyElement.removeFromSuperview()
                
            }
            else {
                
                this.fileItemViews.everyElement.addedAsSubviewToView(this)
                
            }
            
            this.statusLabel.hidden = !this._isShowingError
            bounds
                .IF(this._isDocumentDragging || !hasFiles)
                .rectangleWithHeight(this.dropZone)
                .rectangleWithMinHeight(RHForms.fileUploadCompactDropZoneMinHeight)
                .assignedAsFrameOfView(this.dropZone)
                .ELSE()
                .TRANSFORM((columnBounds: UIRectangle) => {
                    const [leftColumn, rightColumn] = columnBounds.rectanglesByEquallySplittingWidth(
                        2,
                        padding * 0.25
                    )
                    return [
                        leftColumn.framesByDistributingViewsAsColumn(
                            this.fileItemViews,
                            padding * 0.25,
                            this.fileItemViews
                        ).boundingBox(),
                        rightColumn.rectangleWithHeight(this.dropZone)
                            .rectangleWithMinHeight(RHForms.fileUploadCompactDropZoneMinHeight)
                            .assignedAsFrameOfView(this.dropZone)
                    ].boundingBox()
                })
                .ENDIF()
                .IF(this._isShowingError)
                .rectangleForNextRow(padding * 0.5, this.statusLabel)
                .assignedAsFrameOfView(this.statusLabel)
                .ENDIF()
            if (this.bulkDownloadButton.superview) { this.bulkDownloadButton.hidden = YES }
        }
        else {
            // Full layout
            this.statusLabel.hidden = NO
            // this.statusLabel.fontSize = 12
            this.statusLabel.fontSize = RHForms.fileUploadStatusFontSize
            if (this.bulkDownloadButton.superview) {
                this.bulkDownloadButton.hidden = NO
            }
            
            // const dropZoneHeight = padding * 8
            const dropZoneHeight = RHForms.fileUploadFullDropZoneHeight
            
            this.dropZone.frame = bounds.rectangleWithHeight(dropZoneHeight)
            
            this.dropZone.frame
                .rectangleForNextRow(padding, 0)
                .rectangleWithInsets(padding * 0.5, padding * 0.5, 0, 0)
                .rectangleWithHeight(this.statusLabel)
                .assignedAsFrameOfView(this.statusLabel)
                .rectangleWithInsets(-padding * 0.5, -padding * 0.5, 0, 0)
                
                .IF(this.fileItemViews.length > 0)
                .rectangleForNextRow(padding, 0)
                .framesByDistributingViewsAsColumn(
                    this.fileItemViews,
                    padding * 0.5,
                    this.fileItemViews
                ).lastElement
                .ENDIF()
                .rectangleForNextRow(padding * 0.5, RHForms.fileUploadBulkDownloadButtonHeight)
                .assignedAsFrameOfView(this.bulkDownloadButton)
        }

        this._layoutBulkDownloadButtonContent()
    }
    
}
